import React, { useEffect, useMemo } from "react";
import { usePage } from "@inertiajs/inertia-react";
import { useAtom } from "jotai";
import { focusAtom } from "jotai-optics";
import { reportQuestionsAtom } from "@/Stores";
import { LabeledInputCalculatable } from "@/Components/LabledInputCalculatable";

export function InputTypeDistributeForm({ question, values = null }) {
  const errors = usePage().props?.errors || {};
  const errorBag = `reportQuestion.${question.id}`;

  const inputAtom = useMemo(
    () => focusAtom(reportQuestionsAtom, (optic) => optic.prop(question.id)),
    [question],
  );
  const [payload, setPayload] = useAtom(inputAtom);

  useEffect(() => {
    const initialValues = question.inputs.map((input) => {
      let defaultValue = null;

      if (values) {
        defaultValue =
          values
            .find((v) => v.question_input_id === input.id)
            ?.value?.toString() || null;
      }

      return { question_input_id: input.id, value: defaultValue };
    });

    setPayload({
      answers: initialValues,
    });
  }, []);

  const handleFormUpdate = (e, index) => {
    let { name, value } = e.target;

    setPayload((prev) => {
      const input = question.inputs[index];
      const updatedAnswers = [...(prev?.answers || [])];

      if (value.startsWith("00")) {
        value = value.slice(1);
      }

      updatedAnswers[index] = { question_input_id: input.id, [name]: value };

      if (!input.unit) {
        const { total, correctedValue } = calculateTotalAndCorrectedValue(
          updatedAnswers,
          value,
        );

        if (correctedValue !== undefined) {
          updatedAnswers[index] = {
            question_input_id: input.id,
            [name]: correctedValue?.toString(),
          };
        }

        if (total === 100) {
          fillRemainingInputsWithZero(updatedAnswers, question);
        }
      }

      return { ...prev, answers: updatedAnswers };
    });
  };

  const calculateTotalAndCorrectedValue = (updatedAnswers, value) => {
    let total = updatedAnswers.reduce(
      (acc, curr) => acc + Number(curr.value),
      0,
    );

    // As we're on percentage, make sure we do not overflow
    let correctedValue;
    if (total > 100) {
      correctedValue = 100 - (total - Number(value));
      total = 100;
    }

    return { total, correctedValue };
  };

  const fillRemainingInputsWithZero = (updatedAnswers, question) => {
    for (let i = 0; i < updatedAnswers.length; i++) {
      if (!updatedAnswers[i]?.value) {
        updatedAnswers[i] = {
          question_input_id: question.inputs[i].id,
          value: "0",
        };
      }
    }
  };

  if (!question.inputs.length) return null;

  const leftTo100Val = Math.round(
    100 -
      payload?.answers?.reduce(
        (acc, curr) => acc + Number(curr?.value || 0),
        0,
      ),
  );
  const leftTo100 = Number.isFinite(leftTo100Val) ? leftTo100Val : 100;

  return (
    <>
      <span className="mb-1 block text-[14px]">{question.question_text}*</span>

      <div className="flex flex-wrap">
        {question.inputs.map((input, index) => (
          <LabeledInputCalculatable
            key={input.id}
            id={input.id}
            name="value"
            title={`${input.label} (${input.unit ? input.unit : "%"})*`}
            unit={
              !input?.unit &&
              !payload?.answers?.[index]?.value &&
              `${leftTo100}% left`
            }
            unitClass="opacity-50 inline-block text-white bg-slate p-2 text-sm rounded-md leading-none"
            placeholder={`${input.unit ? input.unit : "Percentage used"}`}
            value={payload?.answers?.[index]?.value || ""}
            onChange={(e) => handleFormUpdate(e, index)}
            containerClass={`w-full md:w-1/2 mb-4 ${
              index % 2 !== 0 ? "md:pl-4" : ""
            }`}
            error={
              (errors?.[errorBag] &&
                errors?.[errorBag]?.[`values.${index}.value`]) ||
              errors?.[`${errorBag}.values.${index}.value`]
            }
          />
        ))}
      </div>
    </>
  );
}
